import parseISO from "date-fns/parseISO";
import { isNil, padStart } from "lodash";

export type DateValue = number | string | Date;

/**
 * Converts the given input to a Date instance.
 *
 * The input is assumed to be either:
 * - number of milliseconds since the epoch
 * - an ISO8601 string
 * - a Date instance

 * @param date
 */
export const toDate = (date: DateValue): Date => {
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === "string") {
    return parseISO(date);
  }
  return new Date(date);
};

export function atMidnight(date: DateValue): Date {
  const d = new Date(toDate(date).getTime());
  d.setHours(0);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
}

export function atNoon(date: DateValue): Date {
  const d = new Date(toDate(date).getTime());
  d.setHours(12);
  d.setMinutes(0);
  d.setSeconds(0);
  d.setMilliseconds(0);
  return d;
}

export function atTime(date: DateValue, time: DateValue): Date {
  const d = toDate(date);
  const match = typeof time === "string" && /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (typeof time === "string" && match) {
    d.setHours(parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3] ?? "0", 10), 0);
  } else {
    const t = toDate(time);
    d.setHours(t.getHours(), t.getMinutes(), t.getSeconds(), 0);
  }
  return d;
}

export const atMonth = (date: DateValue, month: number): Date => {
  const d = new Date(toDate(date).getTime());
  d.setMonth(month - 1);
  return d;
};

export function toFirstOfMonth(date: DateValue): Date {
  const d = atMidnight(date);
  d.setDate(1);
  return d;
}

export function toLastOfMonth(date: DateValue): Date {
  const d = toFirstOfMonth(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
}

export function toFirstOfWeek(date: DateValue): Date {
  const d = atMidnight(date);
  if (d.getDay() === 0) {
    d.setDate(d.getDate() - 6);
  } else {
    d.setDate(d.getDate() - (d.getDay() - 1));
  }
  return d;
}

export function toLastOfWeek(date: DateValue): Date {
  const d = atMidnight(date);
  if (d.getDay() === 0) {
    return d;
  } else {
    d.setDate(d.getDate() + (7 - d.getDay()));
  }
  return d;
}

export function plusYears(date: DateValue, n: number): Date {
  const d = new Date(toDate(date).getTime());
  d.setFullYear(d.getFullYear() + n);
  return d;
}

export function plusMonths(date: DateValue, n: number): Date {
  const d = new Date(toDate(date).getTime());
  d.setMonth(d.getMonth() + n);
  return d;
}

export function plusDays(date: DateValue, n: number): Date {
  const d = new Date(toDate(date).getTime());
  d.setDate(d.getDate() + n);
  return d;
}

export function plusHours(date: DateValue, n: number): Date {
  const d = new Date(toDate(date).getTime());
  d.setHours(d.getHours() + n);
  return d;
}

export const getWeekNumber = (date: DateValue): number => {
  // Source: https://weeknumber.com/how-to/javascript
  const d = atMidnight(date);
  // Thursday in the current week decides the year.
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  // January 4 is always in week 1.
  const week1 = new Date(d.getFullYear(), 0, 4);
  // Adjust to Thursday in week 1 and count the number of weeks from date to week1.
  return (
    1 +
    Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
  );
};

export function isSameYear(d1: DateValue, d2: DateValue): boolean {
  return toDate(d1).getFullYear() === toDate(d2).getFullYear();
}

export function isSameMonth(d1: DateValue, d2: DateValue): boolean {
  return toFirstOfMonth(d1).getTime() === toFirstOfMonth(d2).getTime();
}

export function isSameDay(d1: DateValue, d2: DateValue): boolean {
  return atMidnight(d1).getTime() === atMidnight(d2).getTime();
}

export function isBefore(x: DateValue, y: DateValue): boolean {
  return toDate(x) < toDate(y);
}

export function isAfter(x: DateValue, y: DateValue): boolean {
  return toDate(x) > toDate(y);
}

export const isValidDate = (date: DateValue) => !isNaN(toDate(date).getTime());

const timeInputPattern = /^(\d\d):(\d\d)$/;
const lenientTimeInputPattern = /^(\d{1,2}):?(\d{0,2})$/;

export function isValidTime(time: string): boolean {
  const match = timeInputPattern.exec(time);
  if (!match) {
    return false;
  }
  const hh = parseInt(match[1], 10);
  const mm = parseInt(match[2], 10);
  return hh >= 0 && hh < 24 && mm >= 0 && mm < 60;
}

export function expandTimeString(time: string): string {
  const trimmed = time.trim();
  const match = lenientTimeInputPattern.exec(trimmed);
  if (!match) {
    return trimmed;
  }
  const h = match[1];
  const m = match[2];
  const hh = h.length === 1 ? `0${h}` : h;
  const mm = m.length === 2 ? m : m.length === 1 ? `${m}0` : "00";
  return `${hh}:${mm}`;
}

const lenientDateIsoPattern = /(\d{4})-?(\d{1,2})?-?(\d{1,2})?/;

function parseLenientISODatePattern(input: string): Date | null {
  const [, y, m, d] = lenientDateIsoPattern.exec(input) ?? [];
  if (isNil(y)) {
    return null;
  }
  if (isNil(m)) {
    return new Date(parseInt(y, 10), 0, 1);
  }
  if (isNil(d)) {
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  }
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
}

export function parseDateInput(input: string): {
  parsed: Date | null;
  input: string;
  error: boolean;
} {
  const trimmedInput = input.trim();
  const parsed = parseLenientISODatePattern(trimmedInput);
  const expandedInput = parsed ? toLocalDateISOString(parsed) : trimmedInput;
  return { parsed, input: expandedInput, error: !parsed && Boolean(trimmedInput) };
}

export const diffDays = (date1: DateValue, date2: DateValue) => {
  const t1 = toDate(date1);
  const t2 = toDate(date2);
  const diffMillis = Math.abs(t1.getTime() - t2.getTime());
  return Math.ceil(diffMillis / (1000 * 60 * 60 * 24));
};

export const toLocalDateISOString = (date: DateValue): string => {
  const d = toDate(date);
  const year = d.getFullYear();
  const month = padStart(String(d.getMonth() + 1), 2, "0");
  const day = padStart(String(d.getDate()), 2, "0");
  return `${year}-${month}-${day}`;
};

export const toLocalTime = (date: string | Date): string => {
  if (typeof date === "string" && isValidTime(date)) {
    return date;
  }
  const d = toDate(date);
  const hours = padStart(String(d.getHours()), 2, "0");
  const minutes = padStart(String(d.getMinutes()), 2, "0");
  return `${hours}:${minutes}`;
};

export function toLocalDateTimeISOString(date: DateValue): string {
  const d = toDate(date);
  return `${toLocalDateISOString(d)}T${toLocalTime(d)}`;
}

export const toYearISOString = (date: DateValue): string => toDate(date).getFullYear().toString();

export const toYearMonthISOString = (date: DateValue): string => {
  const d = toDate(date);
  return `${d.getFullYear()}-${padStart(String(d.getMonth() + 1), 2, "0")}`;
};

export const combineDateAndTime = (dateString: string, timeString: string) => {
  const date = toDate(dateString);
  if (timeString) {
    const [hours, minutes] = timeString.split(":");
    date.setHours(Number(hours));
    date.setMinutes(Number(minutes));
  } else {
    date.setHours(0);
    date.setMinutes(0);
  }
  return date.toISOString();
};

export const toIsoString = (dateString: string) => {
  return toDate(dateString).toISOString();
};

export const DEFAULT_TIME = "12:00";

export const nextAvailableTime = (date: string | null | undefined, lastTransactionDate: string) => {
  if (date !== toLocalDateISOString(lastTransactionDate)) {
    return DEFAULT_TIME;
  }
  const maxTime = toLocalTime(lastTransactionDate);
  if (maxTime < DEFAULT_TIME) {
    return DEFAULT_TIME;
  }
  const newDate = toDate(lastTransactionDate);
  newDate.setMinutes(newDate.getMinutes() + 1);
  return toLocalTime(newDate);
};

export function parseTimeInput(input: string): {
  parsed: string | null;
  input: string;
  error: boolean;
} {
  const expanded = expandTimeString(input);
  const isValid = isValidTime(expanded);
  return { parsed: isValid ? expanded : null, input: input, error: !isValid };
}

export const today = (): Date => atMidnight(Date.now());
export const yesterday = (): Date => plusDays(atMidnight(Date.now()), -1);

export const isToday = (date: DateValue) => {
  const today = new Date();
  date = toDate(date);
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const isYesterday = (date: DateValue) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  date = toDate(date);
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  );
};

export function toGoogleDate(date: string) {
  return toDate(date).toISOString().replaceAll(/[:-]/g, "").split(".")[0] + "Z";
}
